import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, FileText, Loader2 } from "lucide-react";
import AdminShell from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import { api, apiBaseUrl, getErrorMessage } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const STATUSES = ["pending_review", "approved", "rejected"];

export default function AdminKyc() {
  const nav = useNavigate();
  const { admin } = useAdminAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("pending_review");
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [actionErr, setActionErr] = useState("");
  const [reason, setReason] = useState("");
  const pager = useAdminPager();

  useEffect(() => {
    if (admin === false) nav("/admin/login");
  }, [admin, nav]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/kyc/list", {
        params: { status, skip: pager.skip, limit: pager.limit },
      });
      const { items, total } = unwrapAdminList(data);
      setList(items);
      pager.setTotal(total);
    } catch (e) {
      setActionErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => {
    if (admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, admin, pager.page]);

  async function openUser(u) {
    setSelected(u);
    setDocs([]);
    setReason("");
    setActionErr("");
    try {
      const { data } = await api.get(`/api/admin/kyc/${u.user.id}/documents`);
      setDocs(data);
    } catch (e) {
      setActionErr(getErrorMessage(e));
    }
  }

  async function approve() {
    if (!selected) return;
    setActionErr("");
    try {
      await api.post(`/api/admin/kyc/${selected.user.id}/approve`);
      setSelected(null);
      load();
    } catch (e) {
      setActionErr(getErrorMessage(e));
    }
  }

  async function reject() {
    if (!selected) return;
    setActionErr("");
    if (!reason.trim()) return setActionErr("Please provide a reason");
    try {
      const form = new FormData();
      form.append("reason", reason);
      await api.post(`/api/admin/kyc/${selected.user.id}/reject`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSelected(null);
      load();
    } catch (e) {
      setActionErr(getErrorMessage(e));
    }
  }

  if (!admin) {
    return <div className="min-h-screen grid place-items-center text-[#8892A8]">Loading…</div>;
  }

  return (
    <AdminShell title="KYC Review">
      <main className="-mt-4">
        <div className="mt-4 flex gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              data-testid={`admin-filter-${s}`}
              className={`px-4 py-2 rounded-full text-[13px] font-semibold transition ${
                status === s
                  ? "bg-[#F0B83A] text-[#0A0F1F]"
                  : "bg-[#0F1626] border border-[#1E2845] text-[#8892A8] hover:text-[#E5E9F2]"
              }`}
            >
              {s.replace("_", " ")}
            </button>
          ))}
        </div>

        <div className="mt-6 bg-[#0F1626] rounded-[16px] border border-[#1E2845] overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[#8892A8]">
              <Loader2 className="inline animate-spin mr-2" size={16} /> Loading…
            </div>
          ) : list.length === 0 ? (
            <div className="p-10 text-center text-[#8892A8] text-[14px]">No submissions.</div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-[#0F1626] text-[#8892A8] text-left">
                <tr>
                  <th className="px-5 py-3 font-semibold">User</th>
                  <th className="px-5 py-3 font-semibold">Full name</th>
                  <th className="px-5 py-3 font-semibold">Mobile</th>
                  <th className="px-5 py-3 font-semibold">Submitted</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--cm-border)]">
                {list.map((row) => (
                  <tr key={row.user.id} data-testid={`admin-row-${row.user.id}`}>
                    <td className="px-5 py-3 text-white font-medium">{row.user.email}</td>
                    <td className="px-5 py-3 text-[#8892A8]">{row.profile?.full_name || "—"}</td>
                    <td className="px-5 py-3 text-[#8892A8]">{row.user.mobile_number || "—"}</td>
                    <td className="px-5 py-3 text-[#8892A8]">
                      {row.user.kyc_submitted_at?.slice(0, 10) || "—"}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => openUser(row)}
                        className="text-[#3B82F6] font-semibold hover:underline"
                        data-testid={`admin-review-${row.user.id}`}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <AdminPager
            page={pager.page}
            total={pager.total}
            pageSize={pager.pageSize}
            loading={loading}
            onPage={pager.setPage}
            testId="kyc-pager"
          />
        </div>

      {/* Review modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-[#0A0F1F]/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setSelected(null)}
          data-testid="admin-review-modal"
        >
          <div
            className="bg-[#0F1626] w-full max-w-[820px] max-h-[90vh] overflow-y-auto rounded-[18px] p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[22px] font-bold text-white">
                  {selected.profile?.full_name || selected.user.email}
                </h2>
                <p className="text-[13px] text-[#8892A8] mt-1">{selected.user.email}</p>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#8892A8]">
                <X size={20} />
              </button>
            </div>

            {selected.profile && (
              <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-3 text-[13px]">
                {[
                  ["DOB", selected.profile.date_of_birth],
                  ["Gender", selected.profile.gender],
                  ["Nationality", selected.profile.nationality],
                  ["Occupation", selected.profile.occupation],
                  ["Annual income", selected.profile.annual_income_range],
                  ["Source of funds", selected.profile.source_of_funds],
                  ["PAN", selected.profile.pan_number || "—"],
                  ["PEP", selected.profile.pep_declaration ? "Yes" : "No"],
                  ["City", selected.profile.address?.city],
                  ["State", selected.profile.address?.state],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[#8892A8] text-[11px] uppercase tracking-wider">{k}</div>
                    <div className="text-white font-medium">{v || "—"}</div>
                  </div>
                ))}
              </div>
            )}

            {selected.profile?.verification_mode === "signzy" && (
              <div className="mt-6 rounded-[12px] border border-emerald-500/30 bg-emerald-500/10 p-4 text-[13px] space-y-2">
                <div className="font-bold text-emerald-300">Signzy verification</div>
                <div className="text-[#E5E9F2]">Status: {selected.profile.signzy_status || "—"}</div>
                {selected.profile.verification?.engine && (
                  <div className="text-[#8892A8]">Engine: {selected.profile.verification.engine}</div>
                )}
                {selected.profile.digilocker_request_id && (
                  <div className="text-[#8892A8] font-mono text-[11px]">Request: {selected.profile.digilocker_request_id}</div>
                )}
                {selected.profile.face_match && (
                  <div className={selected.profile.face_match.verified ? "text-emerald-300" : "text-red-300"}>
                    Face match: {selected.profile.face_match.verified ? "passed" : "failed"}
                    {selected.profile.face_match.match_percentage != null && ` (${selected.profile.face_match.match_percentage}%)`}
                  </div>
                )}
                {selected.profile.pan_verify && (
                  <div className={selected.profile.pan_verify.verified ? "text-emerald-300" : "text-amber-300"}>
                    PAN verify: {selected.profile.pan_verify.verified ? "passed" : "failed"}
                  </div>
                )}
              </div>
            )}

            <div className="mt-8">
              <h3 className="text-[15px] font-bold text-white mb-3">Documents</h3>
              <div className="grid grid-cols-2 gap-3">
                {docs.map((d) => (
                  <a
                    key={d.doc_type}
                    href={`${apiBaseUrl()}/api/admin/kyc/${selected.user.id}/documents/${d.doc_type}/file`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 p-3 border border-[var(--cm-border)] rounded-[10px] hover:border-[#3B82F6] hover:bg-[#1A2238] transition"
                    data-testid={`admin-doc-${d.doc_type}`}
                  >
                    <FileText size={18} className="text-[#3B82F6]" />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-white capitalize">
                        {d.doc_type.replace(/_/g, " ")}
                      </div>
                      <div className="text-[11px] text-[#8892A8]">{d.file_name}</div>
                    </div>
                  </a>
                ))}
                {docs.length === 0 && <div className="text-[#8892A8] text-[13px]">No documents.</div>}
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Rejection reason (required to reject)"
                data-testid="admin-reason"
                className="w-full p-3 border border-[#1E2845] bg-[#0A0F1F] text-white placeholder:text-[#5A6582] rounded-[10px] text-[13px] focus:border-[#F0B83A] outline-none min-h-[70px]"
              />
              {actionErr && (
                <div className="text-[13px] text-red-600 bg-red-50 border border-red-100 rounded-[10px] px-4 py-3">
                  {actionErr}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={reject}
                  data-testid="admin-reject"
                  className="flex-1 py-3 rounded-[10px] bg-red-500 text-white font-semibold text-[14px] hover:bg-red-600 flex items-center justify-center gap-2"
                >
                  <X size={16} /> Reject
                </button>
                <button
                  onClick={approve}
                  data-testid="admin-approve"
                  className="flex-1 py-3 rounded-[10px] bg-emerald-500 text-white font-semibold text-[14px] hover:bg-emerald-600 flex items-center justify-center gap-2"
                >
                  <Check size={16} /> Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </main>
    </AdminShell>
  );
}
